// @flow
import { createSelector } from 'reselect';
import { getAccountsById } from 'data/accounts/selectors';
import { getBalanceAdjustmentString } from 'data/chartOfAccounts/utils';
import { Map as ImmutableMap } from 'immutable';
import { CoaTypeEnum, mkChartOfAccount } from 'data/chartOfAccounts/types';
import * as themesSelectors from 'themes/themesSelectors';
import { REDUCER_KEY } from './reducer';
import { CategoryTypeEnum } from './types';
import type { CategoriesStore } from './types';

export const getCategoryStore = (state: any): CategoriesStore => state[REDUCER_KEY];
export const getCategoriesById = (state: any) => getCategoryStore(state).resourcesById;
export const getLoadPending = (state: any) => getCategoryStore(state).loadPending;
export const getIsLoading = (state: any) => getCategoryStore(state).isLoading;
export const getLastSyncDate = (state: any) => getCategoryStore(state).lastSyncDate;

export const getCategories = createSelector(
  getCategoriesById,
  (categoriesById) => categoriesById.toList()
);

/*
 * The selectors below operate directly on the accounts datastore (getAccountStore)
 */

export const getCoaStringSelector = createSelector(
  (state, props) => props.coa,
  (state, props) => props.noParent,
  (state) => state,
  (state) => getAccountsById(state),
  (state) => themesSelectors.getCurrentTheme(state),
  (coa: any, noParent: any, state: any, accountsById: any, theme: any) => {

    if (coa) {
      if (coa.type === CoaTypeEnum.UNCATEGORIZED) {
        return 'Uncategorized';
      }
      if (coa.type === CoaTypeEnum.ACCOUNT) {
        const acct = accountsById.get(coa.id);
        const { components } = theme;

        return `${components.register.transferDelimeter1}${(acct ? acct.name : 'Unsynced Account')}${components.register.transferDelimeter2}`;
      }
      if (coa.type === CoaTypeEnum.BALANCE_ADJUSTMENT) {
        return getBalanceAdjustmentString(coa.id);
      }

      return getCategoryString(state, coa.id, noParent);
    }
    return '';

  }
);

//-------------------------------------------------------
// getCategoryString
//
export function getCategoryString(state: any, id: string, noParent: boolean = false, depth: number = 0) {
  let str = '';

  if (depth && depth > 40) {
    return 'MAXDEPTH_EXCEEDED';
  }

  const catObj = getCategoriesById(state).get(id);

  if (catObj) {
    const parentId = catObj.get('parentId');
    str = catObj.get('name');
    if (!noParent && parentId && (parentId !== '0')) {
      str = `${getCategoryString(state, parentId, noParent, depth + 1)}:${catObj.get('name')}`;
    }
  } else {
    str = 'NOTFOUND';
  }
  return str;
}
//-------------------------------------------------------
// getCategoryForId
//
export function getCategoryForId(state: any, id: string) {

  return getCategoriesById(state).get(id);
}

export function isIncomeCat(state: any, id: string) {
  const cat = getCategoryForId(state, id);

  return (cat && cat.type === CategoryTypeEnum.INCOME);
}

export function isExpenseCat(state: any, id: string) {
  const cat = getCategoryForId(state, id);

  return (cat && cat.type === 'EXPENSE');
}

export const filterCategoriesByGroup = createSelector(
  (categoriesById, _categoryGroup) => categoriesById,
  (_categoriesById, categoryGroup) => categoryGroup,
  (categoriesById: any, categoryGroup: any) => categoriesById && categoryGroup && categoryGroup.coas &&
    categoriesById.filter((category) => categoryGroup.coas.find((coa) => coa.type === CoaTypeEnum.CATEGORY && coa.id === category.id)),
);

// returns categories sub-tree that holds categoryLeaves
export const filterCategoryTreeByLeaves = createSelector(
  (categoriesById, _categoryLeaves) => categoriesById,
  (_categoriesById, categoryLeaves) => categoryLeaves,
  (categoriesById: any, categoryLeaves: any) => (new ImmutableMap()).withMutations((categoryTree) => {
    forEachSubCategory(categoriesById, '0', true, (category, path) => {
      if (categoryLeaves.has(category.id)) {
        categoryTree.set(category.id, category);
        path.forEach((parentCategory) => categoryTree.set(parentCategory.id, parentCategory));
      }
    });
  }),
);

// iterates recursively over categories
export const forEachSubCategory = (categories: any, parentCategoryId: string, preOrder: boolean, callback: any, path: any) => {
  const childCategories = categories.filter((category) => category.parentId === parentCategoryId);
  childCategories.forEach((category) => {
    if (preOrder) {
      callback(category, path || []);
    }
    forEachSubCategory(categories, category.id, preOrder, callback, [...(path || []), category]);
    if (!preOrder) {
      callback(category, path || []);
    }
  });
};

export const getIncomeCOAIds = createSelector(
  getCategoriesById,
  (categories: any) => categories.filter((category) => category.type === CategoryTypeEnum.INCOME).map((category) => category.id).toSet()
);

export const getIncomeCOAs = createSelector(
  getCategoriesById,
  (categories: any) =>
    categories
      .filter((category) => category.type === CategoryTypeEnum.INCOME)
      .map((category) => mkChartOfAccount({
        type: CoaTypeEnum.CATEGORY,
        id: category.id,
      }))
      .toSet(),
);

export const getExpenseCOAs = createSelector(
  getCategoriesById,
  (categories: any) =>
    categories
      .filter((category) => category.type === CategoryTypeEnum.EXPENSE)
      .map((category) => mkChartOfAccount({
        type: CoaTypeEnum.CATEGORY,
        id: category.id,
      }))
      .toSet(),
);

export const getSpendingCOAs = createSelector(
  getExpenseCOAs,
  (expenseCOAs: any) => expenseCOAs.add(mkChartOfAccount({
    type: CoaTypeEnum.UNCATEGORIZED,
    id: '0',
  })),
);

export const getPersonalIncomeCategory = createSelector(
  getCategoriesById,
  (categories: any) => categories.find((category) => category.knownCategoryId === '1800')
  || categories.find((category) => category.name === 'Personal Income' && category.type === CategoryTypeEnum.INCOME),
);

export const getDefaultWatchlistCategory = (state) => {
  const categories = getCategoriesById(state);
  // this gets the 'Dining and Drinks' category based on known id
  const eatingOut = categories.find((category) => category.knownCategoryId === '2250000000');
  return eatingOut ? categories.filter((category) => category.parentId === eatingOut.id || category.id === eatingOut.id) : undefined;
}
